import type { Shadows } from '@mui/material';
import { createTheme, alpha } from '@mui/material';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    tinted: true;
  }
}

export const theme = ({ primary = '#2748FB', secondary = '#DEE3FD', background = '#F4F7FE' }: Record<string, string | undefined>) =>
  createTheme({
    breakpoints: {
      values: {
        xs: 320,
        sm: 640,
        md: 768,
        lg: 1024,
        xl: 1280,
        xxl: 1536,
      },
    },
    palette: {
      primary: {
        main: primary,
        dark: '#2B3674',
        light: '#707EAE',
      },
      secondary: {
        main: secondary,
        dark: '#DEE3FD',
        light: '#E6E9F9',
      },
      background: {
        default: background,
        paper: '#FFFFFF',
      },
      error: {
        main: '#E74C3C',
        light: '#E74C3C12',
      },
      warning: {
        main: '#F39C12',
        light: '#E74C3C12',
      },
      success: {
        main: '#2ECC71',
        light: '#2ECC7112',
      },
      text: {
        primary: '#2B3674',
        secondary: '#707EAE',
      },
    },
    spacing: (factor: number | keyof { [key: number]: string }): string => {
      const customSpacing = {
        68: '17rem',
        15: '3.75rem',
      };

      if (typeof factor === 'number') return `${factor * 8}px`;
      return customSpacing[factor] || `${factor * 8}px`;
    },
    typography: {
      fontFamily: ['Fira Sans', 'sans-serif'].join(','),
      h1: {
        fontWeight: 600,
        fontSize: '32px',
        lineHeight: '38.4px',
      },
      h2: {
        fontWeight: 600,
        fontSize: '28px',
        lineHeight: '33.6px',
      },
      h3: {
        fontWeight: 600,
        fontSize: '24px',
        lineHeight: '28.8px',
      },
      h4: {
        fontWeight: 600,
        fontSize: '22px',
        lineHeight: '26.4px',
      },
      h5: {
        fontWeight: 600,
        fontSize: '18px',
        lineHeight: '21.6px',
      },
      h6: {
        fontWeight: 600,
        fontSize: '16px',
        lineHeight: '19.2px',
      },
      subtitle1: {
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '24px',
      },
      subtitle2: {
        fontSize: '12px',
        fontWeight: '400',
        lineHeight: '14.4px',
      },
      body1: {
        fontSize: '14px',
        lineHeight: '24px',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': {
            '::-webkit-scrollbar': {
              width: '16px',
            },
            '::-webkit-scrollbar-thumb': {
              backgroundColor: '#D9D9D9',
              borderRadius: '10px',
              border: '4px solid #F9F9F9',
            },
            '::-webkit-scrollbar-track': {
              backgroundColor: '#F9F9F9',
            },
          },
          '.picker-dialog': {
            zIndex: '20000 !important',
            left: '50% !important',
            top: '50% !important',
            transform: 'translate(-50%, -50%) !important',
            boxShadow: '0px 10px 50px rgba(0,0,0,0.4) !important',
            borderRadius: '8px !important',
            overflow: 'hidden !important',
          },
          '.picker-dialog-bg': {
            zIndex: '19999 !important',
          },
          '.picker-dialog-content': {
            width: '85vw !important',
            height: '85vh !important',
          },
          '.picker-dialog-frame': {
            width: '100% !important',
            height: '100% !important',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            fontSize: '14px',
            lineHeight: '24px',
            fontWeight: 700,
            paddingTop: '8px',
            paddingBottom: '8px',
            textTransform: 'initial',
            borderRadius: '12px',
          },
        },
        variants: [
          {
            props: { variant: 'tinted' },
            style: ({ theme: muiTheme }) => ({
              backgroundColor: alpha(muiTheme.palette.primary.main, 0.12),
              color: muiTheme.palette.primary.main,
              '& .MuiButton-startIcon > svg': {
                color: 'inherit',
              },
              '&:hover': {
                backgroundColor: alpha(muiTheme.palette.primary.main, 0.22),
              },
              '&.Mui-disabled': {
                backgroundColor: alpha(muiTheme.palette.primary.main, 0.06),
                color: muiTheme.palette.text.disabled,
              },
            }),
          },
        ],
      },
      MuiSelect: {
        styleOverrides: {
          root: ({ theme: muiTheme }) => ({
            boxShadow: 'none',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: secondary,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: secondary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: secondary,
            },
            '& .MuiOutlinedInput-input': {
              borderRadius: '4px',
              padding: '5px',
              [muiTheme.breakpoints.up('md')]: {
                padding: '8px',
              },
            },
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          body: {
            height: '50px',
            padding: '18px 15px',
            borderBottom: '1px solid #DEE3FD',
          },
          head: {
            padding: '4px 15px',
            borderBottom: 'none',
            backgroundColor: '#F4F7FE',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:last-child td': {
              borderBottom: 0,
            },
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: '#2B3674',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.12))',
          },
        },
      },
      MuiDrawer: {
        defaultProps: {},
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ theme: muiTheme }) => ({
            '& .MuiOutlinedInput-root': {
              borderRadius: '4px',
              padding: '5px',
              [muiTheme.breakpoints.up('md')]: {
                padding: '8px',
              },
              '& fieldset': {
                borderColor: secondary,
              },
              '&:hover fieldset': {
                borderColor: secondary,
              },
              '&.Mui-disabled fieldset': {
                borderColor: secondary,
              },
              '&.Mui-focused fieldset': {
                borderColor: secondary,
              },
              '&.Mui-error fieldset': {
                borderColor: secondary,
              },
              '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                color: '#857C86',
              },
            },

            '& .MuiOutlinedInput-input': {
              padding: 0,
              '::placeholder': {
                color: '#857C86',
                opacity: 1,
              },

              ':-webkit-autofill,:-webkit-autofill:hover,:-webkit-autofill:focus,:-webkit-autofill:active': {
                borderRadius: 'none',
                fontSize: '16px',
                transition: 'background-color 5000s ease-in-out 0s, color 5000s ease-in-out 0s',
                transitionDelay: 'background-color 5000s, color 5000s',
              },
            },
            '& input[type=number]': {
              MozAppearance: 'textfield',
            },
          }),
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            width: 'auto',
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          root: {
            '& .MuiBadge-badge': {
              // right: -15,
              // top: 11,
              backgroundColor: '#BF1A2F',
              color: '#ffffff',
            },
          },
        },
      },
    },
    shadows: ['none', ' 0px 8px 16px 0px rgba(112, 144, 176, 0.08)', ...Array(24).fill('none')] as Shadows,
  });
